import React from 'react';

interface TableVisualProps {
    tableName: string;
    shape: 'round' | 'rectangle' | 'long';
    capacity: number;
    guests: { name: string; type: string }[];
    isSelected?: boolean;
    isAssignTarget?: boolean;
    onClick?: () => void;
}

export const TableVisual: React.FC<TableVisualProps> = ({
    tableName,
    shape,
    capacity,
    guests,
    isSelected = false,
    isAssignTarget = false,
    onClick,
}) => {
    const seatCount = Math.max(capacity, guests.length);
    const isFull = guests.length >= capacity;
    const isOverCapacity = guests.length > capacity;

    return (
        <div
            className={`relative cursor-pointer transition-all duration-300 group ${
                isSelected ? 'scale-105 z-10' : 'hover:scale-[1.03]'
            } ${isAssignTarget ? 'animate-pulse' : ''}`}
            onClick={onClick}
        >
            {shape === 'round' ? (
                <RoundTable
                    tableName={tableName}
                    capacity={capacity}
                    guests={guests}
                    seatCount={seatCount}
                    isFull={isFull}
                    isOverCapacity={isOverCapacity}
                    isSelected={isSelected}
                    isAssignTarget={isAssignTarget}
                />
            ) : shape === 'long' ? (
                <LongTable
                    tableName={tableName}
                    capacity={capacity}
                    guests={guests}
                    seatCount={seatCount}
                    isFull={isFull}
                    isOverCapacity={isOverCapacity}
                    isSelected={isSelected}
                    isAssignTarget={isAssignTarget}
                />
            ) : (
                <RectangleTable
                    tableName={tableName}
                    capacity={capacity}
                    guests={guests}
                    seatCount={seatCount}
                    isFull={isFull}
                    isOverCapacity={isOverCapacity}
                    isSelected={isSelected}
                    isAssignTarget={isAssignTarget}
                />
            )}
        </div>
    );
};

interface ShapeProps {
    tableName: string;
    capacity: number;
    guests: { name: string; type: string }[];
    seatCount: number;
    isFull: boolean;
    isOverCapacity: boolean;
    isSelected: boolean;
    isAssignTarget: boolean;
}

const RoundTable: React.FC<ShapeProps> = ({
    tableName,
    capacity,
    guests,
    seatCount,
    isFull,
    isOverCapacity,
    isSelected,
    isAssignTarget,
}) => {
    const svgSize = 280;
    const cx = svgSize / 2;
    const cy = svgSize / 2;
    const tableRadius = 60;
    const seatRadius = 14;
    const seatOrbitRadius = tableRadius + seatRadius + 8;

    const seats = Array.from({ length: seatCount }, (_, i) => {
        const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2;
        const x = cx + seatOrbitRadius * Math.cos(angle);
        const y = cy + seatOrbitRadius * Math.sin(angle);
        const guest = guests[i];
        return { x, y, guest, angle };
    });

    const nameOrbitRadius = seatOrbitRadius + seatRadius + 16;

    return (
        <svg viewBox={`0 0 ${svgSize} ${svgSize}`} className="w-full h-auto max-w-[280px] mx-auto">
            {/* Table surface */}
            <circle
                cx={cx}
                cy={cy}
                r={tableRadius}
                className={`transition-all duration-300 ${
                    isAssignTarget
                        ? 'fill-primary/20 stroke-primary'
                        : isSelected
                        ? 'fill-primary-light/40 stroke-primary'
                        : 'fill-white stroke-gray-200 group-hover:stroke-primary/40 group-hover:fill-primary/5'
                }`}
                strokeWidth={2}
            />

            {/* Table name */}
            <text
                x={cx}
                y={cy - 8}
                textAnchor="middle"
                className="fill-secondary text-[13px] font-bold"
                style={{ fontFamily: 'var(--font-serif)' }}
            >
                {tableName}
            </text>

            {/* Capacity */}
            <text
                x={cx}
                y={cy + 10}
                textAnchor="middle"
                className={`text-[10px] font-bold ${
                    isOverCapacity ? 'fill-error' : isFull ? 'fill-primary-dark' : 'fill-secondary-light'
                }`}
            >
                {guests.length}/{capacity}
            </text>

            {/* Seats */}
            {seats.map((seat, i) => (
                <g key={i}>
                    <circle
                        cx={seat.x}
                        cy={seat.y}
                        r={seatRadius}
                        className={`transition-all duration-200 ${
                            seat.guest
                                ? seat.guest.type === 'plusOne'
                                    ? 'fill-purple-100 stroke-purple-300'
                                    : seat.guest.type === 'child'
                                    ? 'fill-amber-100 stroke-amber-300'
                                    : 'fill-primary-light/60 stroke-primary/40'
                                : 'fill-gray-50 stroke-gray-200 stroke-dasharray-[3,3]'
                        }`}
                        strokeWidth={1.5}
                    />
                    {seat.guest && (
                        <text
                            x={cx + nameOrbitRadius * Math.cos(seats[i].angle)}
                            y={cy + nameOrbitRadius * Math.sin(seats[i].angle)}
                            textAnchor={
                                Math.abs(Math.cos(seats[i].angle)) < 0.3
                                    ? 'middle'
                                    : Math.cos(seats[i].angle) > 0
                                    ? 'start'
                                    : 'end'
                            }
                            dominantBaseline="central"
                            className="fill-secondary text-[9px]"
                            style={{ fontFamily: 'var(--font-sans)' }}
                        >
                            {truncateName(seat.guest.name)}
                        </text>
                    )}
                </g>
            ))}
        </svg>
    );
};

const RectangleTable: React.FC<ShapeProps> = ({
    tableName,
    capacity,
    guests,
    seatCount,
    isFull,
    isOverCapacity,
    isSelected,
    isAssignTarget,
}) => {
    const svgWidth = 260;
    const svgHeight = 280;
    const tableW = 100;
    const tableH = 70;
    const tx = (svgWidth - tableW) / 2;
    const ty = (svgHeight - tableH) / 2;
    const seatR = 13;
    const seatGap = 6;

    // Distribute seats around 4 sides
    const perSide = Math.ceil(seatCount / 4);
    const topCount = perSide;
    const bottomCount = perSide;
    const leftCount = Math.min(Math.ceil((seatCount - topCount - bottomCount) / 2), Math.max(1, Math.floor((seatCount - topCount - bottomCount) / 2)));
    const rightCount = seatCount - topCount - bottomCount - leftCount;

    const seatPositions: { x: number; y: number; labelX: number; labelY: number; anchor: 'start' | 'middle' | 'end' }[] = [];

    // Top
    for (let i = 0; i < topCount; i++) {
        const x = tx + (tableW / (topCount + 1)) * (i + 1);
        const y = ty - seatR - seatGap;
        seatPositions.push({ x, y, labelX: x, labelY: y - seatR - 6, anchor: 'middle' });
    }
    // Bottom
    for (let i = 0; i < bottomCount; i++) {
        const x = tx + (tableW / (bottomCount + 1)) * (i + 1);
        const y = ty + tableH + seatR + seatGap;
        seatPositions.push({ x, y, labelX: x, labelY: y + seatR + 12, anchor: 'middle' });
    }
    // Left
    for (let i = 0; i < Math.max(0, leftCount); i++) {
        const x = tx - seatR - seatGap;
        const y = ty + (tableH / (Math.max(1, leftCount) + 1)) * (i + 1);
        seatPositions.push({ x, y, labelX: x - seatR - 6, labelY: y, anchor: 'end' });
    }
    // Right
    for (let i = 0; i < Math.max(0, rightCount); i++) {
        const x = tx + tableW + seatR + seatGap;
        const y = ty + (tableH / (Math.max(1, rightCount) + 1)) * (i + 1);
        seatPositions.push({ x, y, labelX: x + seatR + 6, labelY: y, anchor: 'start' });
    }

    return (
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-auto max-w-[260px] mx-auto">
            <rect
                x={tx}
                y={ty}
                width={tableW}
                height={tableH}
                rx={8}
                className={`transition-all duration-300 ${
                    isAssignTarget
                        ? 'fill-primary/20 stroke-primary'
                        : isSelected
                        ? 'fill-primary-light/40 stroke-primary'
                        : 'fill-white stroke-gray-200 group-hover:stroke-primary/40 group-hover:fill-primary/5'
                }`}
                strokeWidth={2}
            />
            <text
                x={tx + tableW / 2}
                y={ty + tableH / 2 - 6}
                textAnchor="middle"
                className="fill-secondary text-[13px] font-bold"
                style={{ fontFamily: 'var(--font-serif)' }}
            >
                {tableName}
            </text>
            <text
                x={tx + tableW / 2}
                y={ty + tableH / 2 + 10}
                textAnchor="middle"
                className={`text-[10px] font-bold ${
                    isOverCapacity ? 'fill-error' : isFull ? 'fill-primary-dark' : 'fill-secondary-light'
                }`}
            >
                {guests.length}/{capacity}
            </text>

            {seatPositions.map((pos, i) => {
                const guest = guests[i];
                return (
                    <g key={i}>
                        <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={seatR}
                            className={`transition-all duration-200 ${
                                guest
                                    ? guest.type === 'plusOne'
                                        ? 'fill-purple-100 stroke-purple-300'
                                        : guest.type === 'child'
                                        ? 'fill-amber-100 stroke-amber-300'
                                        : 'fill-primary-light/60 stroke-primary/40'
                                    : 'fill-gray-50 stroke-gray-200 stroke-dasharray-[3,3]'
                            }`}
                            strokeWidth={1.5}
                        />
                        {guest && (
                            <text
                                x={pos.labelX}
                                y={pos.labelY}
                                textAnchor={pos.anchor}
                                dominantBaseline="central"
                                className="fill-secondary text-[9px]"
                                style={{ fontFamily: 'var(--font-sans)' }}
                            >
                                {truncateName(guest.name)}
                            </text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

const LongTable: React.FC<ShapeProps> = ({
    tableName,
    capacity,
    guests,
    seatCount,
    isFull,
    isOverCapacity,
    isSelected,
    isAssignTarget,
}) => {
    const svgWidth = 420;
    const svgHeight = 200;
    const tableW = 300;
    const tableH = 50;
    const tx = (svgWidth - tableW) / 2;
    const ty = (svgHeight - tableH) / 2;
    const seatR = 13;
    const seatGap = 6;

    const topCount = Math.ceil(seatCount / 2);
    const bottomCount = seatCount - topCount;

    const seatPositions: { x: number; y: number; labelX: number; labelY: number; anchor: 'start' | 'middle' | 'end' }[] = [];

    // Top row
    for (let i = 0; i < topCount; i++) {
        const x = tx + (tableW / (topCount + 1)) * (i + 1);
        const y = ty - seatR - seatGap;
        seatPositions.push({ x, y, labelX: x, labelY: y - seatR - 6, anchor: 'middle' });
    }
    // Bottom row
    for (let i = 0; i < bottomCount; i++) {
        const x = tx + (tableW / (bottomCount + 1)) * (i + 1);
        const y = ty + tableH + seatR + seatGap;
        seatPositions.push({ x, y, labelX: x, labelY: y + seatR + 12, anchor: 'middle' });
    }

    return (
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-auto max-w-[420px] mx-auto">
            <rect
                x={tx}
                y={ty}
                width={tableW}
                height={tableH}
                rx={8}
                className={`transition-all duration-300 ${
                    isAssignTarget
                        ? 'fill-primary/20 stroke-primary'
                        : isSelected
                        ? 'fill-primary-light/40 stroke-primary'
                        : 'fill-white stroke-gray-200 group-hover:stroke-primary/40 group-hover:fill-primary/5'
                }`}
                strokeWidth={2}
            />
            <text
                x={tx + tableW / 2}
                y={ty + tableH / 2 - 6}
                textAnchor="middle"
                className="fill-secondary text-[13px] font-bold"
                style={{ fontFamily: 'var(--font-serif)' }}
            >
                {tableName}
            </text>
            <text
                x={tx + tableW / 2}
                y={ty + tableH / 2 + 10}
                textAnchor="middle"
                className={`text-[10px] font-bold ${
                    isOverCapacity ? 'fill-error' : isFull ? 'fill-primary-dark' : 'fill-secondary-light'
                }`}
            >
                {guests.length}/{capacity}
            </text>

            {seatPositions.map((pos, i) => {
                const guest = guests[i];
                return (
                    <g key={i}>
                        <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={seatR}
                            className={`transition-all duration-200 ${
                                guest
                                    ? guest.type === 'plusOne'
                                        ? 'fill-purple-100 stroke-purple-300'
                                        : guest.type === 'child'
                                        ? 'fill-amber-100 stroke-amber-300'
                                        : 'fill-primary-light/60 stroke-primary/40'
                                    : 'fill-gray-50 stroke-gray-200 stroke-dasharray-[3,3]'
                            }`}
                            strokeWidth={1.5}
                        />
                        {guest && (
                            <text
                                x={pos.labelX}
                                y={pos.labelY}
                                textAnchor={pos.anchor}
                                dominantBaseline="central"
                                className="fill-secondary text-[9px]"
                                style={{ fontFamily: 'var(--font-sans)' }}
                            >
                                {truncateName(guest.name)}
                            </text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

function truncateName(name: string, maxLen = 14): string {
    if (name.length <= maxLen) return name;
    // Try first name only
    const first = name.split(' ')[0];
    if (first.length <= maxLen) return first;
    return first.substring(0, maxLen - 1) + '…';
}
